// The science page's chapter links (sections/chapter-links.liquid): the docked bar and the back-to-top button.
// - [data-stay-dock] (setting "Stick to the top over the chapters"): over the chapters (from the row's pills passing
//   under the bar's place, so a jump to the first chapter shows the bar, to the end of the last link's target, which
//   leaves at the bar's bottom) the row's own list, not a copy, docks to the top of the
//   screen (.is-docked, then .is-shown: base.css "Stay motion"), and html.stay-dock-shown keeps the sticky header in
//   its hidden state (base.css, "section-header"; the header's script is untouched). The nav keeps its height in the
//   page, so nothing below moves. Leaving, the bar slides away first and undocks after, so its change of position
//   never shows. The pill of the chapter in view gets aria-current="true"; a row too wide for the screen scrolls
//   sideways to it. Keyboard focus in a chapter that would land under the bar is scrolled clear of it.
// - .chapter-links__top (setting "Show a back-to-top button"): shown once the row has left the top of the screen
//   (past the opening); moved to the end of <main>, so Tab reaches it after the page's content. Over the footer it stays only where the footer's content leaves its place free (the card's
//   side margin on wide screens), else it goes, so it never covers the footer's text; lifted above Shopify's cookie
//   banner. Over a dark surface (03's Depth) it turns to the base colour with a Depth arrow (.is-on-dark), so it
//   never vanishes into it. It scrolls to the top (at once with reduced motion) and moves focus to the page's <h1>.
(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const nextFrame = (callback) => requestAnimationFrame(() => requestAnimationFrame(callback));
  const SWAP = 250; // --stay-motion-duration-swap

  const onScroll = (update) => {
    let queued = false;
    const run = () => {
      queued = false;
      update();
    };
    const queue = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(run);
    };
    window.addEventListener('scroll', queue, { passive: true });
    window.addEventListener('resize', queue);
    update();
    return queue;
  };

  const initDock = (nav) => {
    const bar = nav.firstElementChild;
    const list = nav.querySelector('.chapter-links__list');
    const pairs = [...nav.querySelectorAll('.chapter-links__link')]
      .map((link) => ({ link, target: document.getElementById(decodeURIComponent(link.hash.slice(1))) }))
      .filter((pair) => pair.target);
    if (!bar || !list || !pairs.length) return;

    let wanted = false;
    let docked = false;
    let shown = false;
    let active = -1;
    let barHeight = 80; // the bar's bottom edge, measured once shown

    // The nav's height in the page, kept while its list is docked
    const reserve = () => {
      const wasDocked = nav.classList.contains('is-docked');
      if (wasDocked) nav.classList.remove('is-docked');
      nav.style.setProperty('--chapter-links-height', `${nav.offsetHeight}px`);
      if (wasDocked) nav.classList.add('is-docked');
    };

    const setActive = (index) => {
      if (index === active) return;
      active = index;
      pairs.forEach(({ link }, i) => {
        if (i === index) link.setAttribute('aria-current', 'true');
        else link.removeAttribute('aria-current');
      });
      const link = pairs[index]?.link;
      if (link && list.scrollWidth > list.clientWidth) {
        list.scrollTo({
          left: link.offsetLeft - (list.clientWidth - link.offsetWidth) / 2,
          behavior: reduced.matches ? 'instant' : 'smooth',
        });
      }
    };

    const show = () => {
      if (!docked) {
        reserve();
        // .is-docking: out of sight at once (no exit transition on the way in)
        nav.classList.add('is-docked', 'is-docking');
        docked = true;
      }
      if (shown) return;
      shown = true;
      // Docked out of sight first, shown a frame later, so it slides in
      nextFrame(() => {
        nav.classList.remove('is-docking');
        if (!wanted) return;
        nav.classList.add('is-shown');
        document.documentElement.classList.add('stay-dock-shown');
        barHeight = bar.offsetTop + bar.offsetHeight; // its bottom edge, the gap above included
      });
    };

    const hide = () => {
      if (!shown) return;
      shown = false;
      nav.classList.remove('is-shown');
      document.documentElement.classList.remove('stay-dock-shown');
      setActive(-1);
      // Undock once it's out of sight (a frame later with reduced motion)
      nextFrame(() => {
        window.setTimeout(() => {
          if (wanted || shown) return;
          nav.classList.remove('is-docked');
          docked = false;
        }, reduced.matches ? 0 : SWAP);
      });
    };

    const update = () => {
      const last = pairs[pairs.length - 1].target;
      // The row's place in the page: the nav keeps its height while docked, its list ends above its bottom padding
      const rowBottom = nav.getBoundingClientRect().bottom - parseFloat(window.getComputedStyle(nav).paddingBottom);
      wanted = rowBottom <= barHeight && last.getBoundingClientRect().bottom > barHeight;
      if (!wanted) {
        hide();
        return;
      }
      show();
      // The chapter in view: the last one whose top has passed a line under the bar
      const line = Math.max(barHeight + 40, window.innerHeight * 0.3);
      let index = 0;
      pairs.forEach(({ target }, i) => {
        if (target.getBoundingClientRect().top <= line) index = i;
      });
      setActive(index);
    };

    reserve();
    onScroll(update);
    window.addEventListener('resize', reserve);

    document.addEventListener('focusin', (event) => {
      if (!shown || nav.contains(event.target) || event.target.closest('.section-header')) return;
      // Only what is on screen but under the bar (not e.g. the <h1> the back-to-top button focuses, far above)
      const { top, bottom } = event.target.getBoundingClientRect();
      if (bottom > 0 && top < barHeight + 8) window.scrollBy({ top: top - barHeight - 16, behavior: 'instant' });
    });
  };

  const initTop = (button) => {
    const nav = button.closest('.shopify-section')?.querySelector('.chapter-links');
    const footer = document.querySelector('.shopify-section-group-footer-group');
    const footerContent = footer?.querySelector('.stay-container, .page-width');
    const heading = document.querySelector('main h1') || document.getElementById('MainContent');
    if (!nav) return;
    // Last in the page's content, so it comes after the chapters in the Tab order (it's rendered with this row)
    // (the theme editor re-renders the section: an earlier, moved button goes)
    const main = button.closest('main');
    main?.querySelectorAll(':scope > .chapter-links__top').forEach((old) => old.remove());
    main?.append(button);
    button.hidden = false;

    // Over the footer, its place is free only beside the footer's content (offsetLeft / offsetTop ignore the lift)
    const clearOfFooter = () => {
      if (!footer || footer.getBoundingClientRect().top > button.offsetTop + button.offsetHeight + 16) return true;
      if (!footerContent) return false;
      const box = footerContent.getBoundingClientRect();
      const contentRight = box.right - parseFloat(window.getComputedStyle(footerContent).paddingRight);
      return button.offsetLeft >= contentRight + 16;
    };

    // The first painted background under the button's centre (its own resting place), dark below 50% lightness
    const onDark = () => {
      const x = button.offsetLeft + button.offsetWidth / 2;
      const y = button.offsetTop + button.offsetHeight / 2;
      for (const element of document.elementsFromPoint(x, y)) {
        if (button.contains(element)) continue;
        const match = window.getComputedStyle(element).backgroundColor.match(/[\d.]+/g);
        if (!match || (match.length > 3 && +match[3] === 0)) continue;
        const [r, g, b] = match.map(Number);
        return (Math.max(r, g, b) + Math.min(r, g, b)) / 2 / 255 < 0.5;
      }
      return false;
    };

    const update = () => {
      button.classList.toggle('is-shown', nav.getBoundingClientRect().top < 0 && clearOfFooter());
      button.classList.toggle('is-on-dark', onDark());
      // Its resting bottom edge kept 16px above the cookie banner
      const bottom = button.offsetTop + button.offsetHeight;
      let limit = window.innerHeight;
      const banner = document.getElementById('shopify-pc__banner');
      if (banner && banner.offsetHeight > 0 && window.getComputedStyle(banner).visibility !== 'hidden') {
        const top = banner.getBoundingClientRect().top;
        if (top < window.innerHeight) limit = Math.min(limit, top - 16);
      }
      button.style.translate = bottom > limit ? `0 ${limit - bottom}px` : '';
    };

    const queue = onScroll(update);
    // Shopify's cookie banner comes in after load and goes when answered
    new MutationObserver(queue).observe(document.body, { childList: true });
    [1000, 3000].forEach((delay) => window.setTimeout(queue, delay));
    document.addEventListener('click', (event) => {
      if (event.target.closest('#shopify-pc__banner')) window.setTimeout(queue, 400);
    });

    button.addEventListener('click', () => {
      window.scrollTo({ top: 0, behavior: reduced.matches ? 'instant' : 'smooth' });
      if (!heading) return;
      if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
      heading.focus({ preventScroll: true });
    });
  };

  const init = () => {
    document.querySelectorAll('.chapter-links[data-stay-dock]').forEach(initDock);
    document.querySelectorAll('.chapter-links__top').forEach(initTop);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('.chapter-links[data-stay-dock]').forEach(initDock);
    event.target.querySelectorAll('.chapter-links__top').forEach(initTop);
  });
})();
